import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import InputFieldFloat from "../components/inputs/InputFieldFloat";
import PasswordInputFloat from "../components/inputs/PasswordInputFloat";
import UploadProfile from "../components/inputs/UploadProfile";
import Checkbox from "../components/inputs/Checkbox";
import { useRegisterMutation, useShowTokenQuery } from "../services/users/auth";
import { useCookies } from "react-cookie";
import { getAndClearReturnUrl, saveReturnUrl } from "../utils/authRedirect";

const EmployeeInvitation = () => {
  const [checked, setChecked] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [image, setImage] = useState();
  const token = searchParams.get("token");
  const [cookies] = useCookies(["cookie-name"]);

  const [register] = useRegisterMutation();
  const { data: showToken } = useShowTokenQuery(token);

  let navigate = useNavigate();
  const { t } = useTranslation(["common", "auth"]);

  const validationSchema = yup.object({
    password: yup
      .string()
      .min(3, t("validations.passwordMin3"))
      .required(t("validations.passwordRequired")),
    name: yup.string().required(t("validations.nameRequired")),
  });

  useEffect(() => {
    const redirectTo = searchParams.get("redirect_to");
    if (redirectTo) saveReturnUrl(redirectTo);
  }, [searchParams]);

  const formik = useFormik({
    initialValues: {
      password: "",
      name: "",
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {
      let formData = new FormData();
      formData.append("invitation_token", token);
      formData.append("[user][password]", values.password);
      formData.append("[user][name_en]", values.name);
      formData.append("[user][name_ar]", values.name);
      if (image) formData.append("[user][avatar]", image);

      const result = await register(formData);
      if (result?.error) {
        toast.error(result?.error?.data?.message);
      } else {
        localStorage.setItem("token", result?.data?.data?.token);
        localStorage.setItem("is_admin", false);
        navigate(getAndClearReturnUrl("/employee"), { replace: true });
      }
    },
  });

  return (
    <div className="login-page">
      <div className="container-fluid">
        <div className="row">
          <div
            className="col-lg-6 login-page__banner"
            style={{
              background: `linear-gradient(180deg, #AF151F00 0%, #AF151F14 100%)`,
            }}
          >
            <div className="content-employee">
              <div
                className="banner-flag-container"
                style={{
                  backgroundImage: `url('/images/banner-flag.svg')`,
                }}
              >
                <img
                  className="logo"
                  alt=""
                  src={cookies?.orgLogo || "/images/Tadarab-logo.png"}
                />
              </div>
              <h1>
                {t("welcomeTadarab")}
                <span>{cookies?.domain}</span>
              </h1>
              <p>{t("startLearning")}</p>
              <ul>
                <li>{t("biggestPlatform")}</li>
                <li>{t("remoteLearning")}</li>
                <li>{t("highQuality")}</li>
                <li>{t("specificTracks")}</li>
              </ul>
            </div>
          </div>
          <div className="col">
            <div className="login-page__content">
              <div className="content-header">
                <h1 className="font-extrabold">
                  {t("auth:completedYourData")}
                </h1>
                <p className="mb-4">{t("auth:oneStepToStart")}</p>
                <UploadProfile
                  title={t("inputs.profilePic")}
                  IconRightAligned
                  image={image}
                  setImage={setImage}
                />
              </div>
              <form
                className="content-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit(e);
                }}
              >
                <InputFieldFloat
                  label={t("inputs.email")}
                  type="email"
                  name="email"
                  value={showToken?.data?.email}
                  disabled
                />

                <InputFieldFloat
                  label={t("inputs.name")}
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.errors.name}
                  touched={formik.touched.name}
                />

                <PasswordInputFloat
                  label={t("inputs.password")}
                  type="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.errors.password}
                  touched={formik.touched.password}
                />
                <Checkbox id="tos" checked={checked} setChecked={setChecked}>
                  <p>
                    {t("iAgree")}
                    <a href="" className="text-link-red under-line">
                      {" "}
                      {t("termsAndConditions")}{" "}
                    </a>
                    {t("tadarabPlatform")}
                  </p>
                </Checkbox>
                <button
                  type="submit"
                  className="btn btn-main-color d-block w-100"
                  disabled={!checked}
                >
                  {t("saveInfo")}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeInvitation;
